import React from "react";

/**
 * ModernTable — unified table primitive (sharp border, dark uppercase header).
 * Stays minimal: accepts columns + rows OR raw children (thead/tbody).
 *
 *  - columns: [{ key, label, className?, align? }]
 *  - rows: [{ ...row, id }] — required when columns is provided
 *  - renderCell: (row, col) => ReactNode  (overrides default row[col.key])
 *  - emptyMessage: string when rows is empty
 *  - dense: smaller padding
 *  - stickyHeader: keep thead sticky on overflow scroll
 *  - children: when no columns provided, lets caller author their own table
 */
export function ModernTable({
  columns,
  rows,
  renderCell,
  emptyMessage = "Aucun résultat.",
  dense = false,
  stickyHeader = false,
  className = "",
  bodyTestId,
  testId,
  children,
  rowKey = "id",
  onRowClick,
  rowClassName,
}) {
  const pad = dense ? "px-3 py-1.5" : "px-4 py-2.5";

  return (
    <div data-testid={testId} className={`border border-zinc-900 bg-white overflow-x-auto ${className}`}>
      <table className="w-full text-sm">
        {columns ? (
          <>
            <thead className={`bg-zinc-900 text-white ${stickyHeader ? "sticky top-0 z-10" : ""}`}>
              <tr>
                {columns.map((c) => (
                  <th
                    key={c.key}
                    className={`text-left ${pad} text-[10px] font-mono tracking-widest uppercase ${c.align === "right" ? "text-right" : ""} ${c.className || ""}`}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody data-testid={bodyTestId}>
              {(rows || []).length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="p-6 text-center text-zinc-500 text-xs">
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                (rows || []).map((row, idx) => {
                  const extraCls = typeof rowClassName === "function" ? rowClassName(row) : rowClassName || "";
                  const clickable = !!onRowClick;
                  return (
                    <tr
                      key={row[rowKey] ?? idx}
                      onClick={clickable ? () => onRowClick(row) : undefined}
                      className={`border-t border-zinc-200 hover:bg-zinc-50 transition-colors ${clickable ? "cursor-pointer" : ""} ${extraCls}`}
                    >
                      {columns.map((c) => (
                        <td
                          key={c.key}
                          className={`${pad} ${c.align === "right" ? "text-right" : ""} ${c.cellClassName || ""}`}
                        >
                          {renderCell ? renderCell(row, c) : row[c.key]}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </>
        ) : (
          children
        )}
      </table>
    </div>
  );
}
